// Viewport-width hook. Layouts use this to swap their desktop chrome for the
// shared <MobileSheet> at narrow widths. The breakpoint matches the CSS
// mobile media queries (768px) so JS structure and CSS layout flip together.
//
// SSR-safe: defaults to false (desktop) when there's no window, then corrects
// on mount. The embed always runs in a browser so this is belt-and-braces.

import { useEffect, useState } from "react";

/** Shared mobile breakpoint. Keep in sync with the `@media (max-width: …)`
 *  rules in styles.css. */
export const MOBILE_BREAKPOINT_PX = 768;

export function useIsMobile(breakpoint: number = MOBILE_BREAKPOINT_PX): boolean {
  const query = `(max-width: ${breakpoint}px)`;
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // Sync once in case the width changed between the initial state and effect.
    setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return isMobile;
}
